import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import MarkEmailReadOutlined from "@mui/icons-material/MarkEmailReadOutlined";

import { useWidget } from "/imports/state/WidgetContext";
import { useUrlFilters } from "/imports/libs/url";
import { CardBackButton } from "/imports/ui/components/shared";
import api from "/imports/api/client";

function EmailSignupView() {
  const navigate = useNavigate();
  const { slug } = useWidget();
  const { filters } = useUrlFilters({});

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  const registerTo = `/${slug}/register${filters.redirect ? `?redirect=${filters.redirect}` : ""}`;
  const loginTo = `/${slug}/login${filters.redirect ? `?redirect=${filters.redirect}` : ""}`;

  const handleBack = () => {
    if (status) {
      setStatus(null);
      return;
    }
    navigate(`/${slug}/signup${filters.redirect ? `?redirect=${filters.redirect}` : ""}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const res = await api.signupLookup(email.trim(), slug);
      setStatus(res?.status || "link_sent");
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" py={4}>
      <Card sx={{ maxWidth: "28.75rem", width: "100%" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 }, "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
          <CardBackButton onClick={handleBack} />
          {status === "link_sent" ? (
            <Box textAlign="center">
              <MarkEmailReadOutlined color="primary" sx={{ fontSize: "3.5rem", mb: 1 }} />
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Check your inbox
              </Typography>
              <Typography variant="body2" color="textSecondary">
                We've sent you a link to finish setting up your account.
              </Typography>
            </Box>
          ) : status === "not_found" ? (
            <Box textAlign="center">
              <Typography variant="h6" fontWeight={600} gutterBottom>
                We couldn't find your email
              </Typography>
              <Alert severity="warning" sx={{ mb: 3, textAlign: "left" }}>
                Your club doesn't have a member on file with <b>{email}</b>. Create an account to get started.
              </Alert>
              <Button component={RouterLink} to={registerTo} variant="contained" fullWidth size="large" sx={{ mb: 2 }}>
                Create an account
              </Button>
              <Link component="button" type="button" variant="body2" onClick={() => setStatus(null)}>
                Try a different email
              </Link>
            </Box>
          ) : status === "already_registered" ? (
            <Box textAlign="center">
              <Typography variant="h6" fontWeight={600} gutterBottom>
                You already have an account
              </Typography>
              <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
                An account already exists for <b>{email}</b>. Please sign in to continue.
              </Alert>
              <Button component={RouterLink} to={loginTo} variant="contained" fullWidth size="large" sx={{ mb: 2 }}>
                Sign in
              </Button>
              <Link component="button" type="button" variant="body2" onClick={() => setStatus(null)}>
                Try a different email
              </Link>
            </Box>
          ) : (
            <>
              <Typography variant="h5" fontWeight={600} textAlign="center" gutterBottom>
                Sign up with your email
              </Typography>
              <Typography variant="body2" color="textSecondary" textAlign="center" mb={3}>
                Already a member? Enter the email your club has on file and we'll send you a link to claim your account.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                />
                <Button type="submit" variant="contained" fullWidth size="large" disabled={loading} sx={{ mt: 3 }}>
                  {loading ? "Sending..." : "Send me a link"}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default EmailSignupView;
